import Analytics from "analytics";
import type { AnalyticsInstance, AnalyticsPlugin, Plugins } from "analytics";

import { mixpanelPlugin } from "../plugins";
import type { MixpanelPluginMethods, MixpanelPluginOptions } from "../plugins";

const MIXPANEL_PLUGIN = "mixpanel" as const;

declare module "analytics" {
  interface Plugins {
    mixpanel: MixpanelPluginMethods;
  }
}

interface MixpanelPluginConfig extends MixpanelPluginOptions {
  type: typeof MIXPANEL_PLUGIN;
}

type PluginConfig = MixpanelPluginConfig;

interface AnalyticsConfig {
  debug?: boolean;
  app?: string;
  plugins?: PluginConfig[];
}

function createAnalyticsClient(
  config: AnalyticsConfig = {}
): AnalyticsClientInstance {
  const {
    debug = false,
    app = "spirii-ui",
    plugins: pluginConfigs = [],
  } = config;

  const plugins: AnalyticsPlugin[] = [];

  pluginConfigs.forEach((pluginConfig) => {
    if (pluginConfig.type === MIXPANEL_PLUGIN) {
      const { token, instanceName, options = {} } = pluginConfig;

      plugins.push(
        mixpanelPlugin({
          token,
          instanceName,
          options: {
            debug,
            ...options,
          },
        })
      );
    }

    // More plugins can be added here in the future
  });

  return Analytics({
    app,
    debug,
    plugins,
  }) as AnalyticsClientInstance;
}

// Type-safe analytics instance with plugin types
type AnalyticsClientInstance = AnalyticsInstance & {
  plugins: Plugins;
};

let analyticsClientInstance: AnalyticsClientInstance | undefined;

function initAnalyticsClient(
  config: AnalyticsConfig = {}
): AnalyticsClientInstance {
  analyticsClientInstance = createAnalyticsClient(config);
  return analyticsClientInstance;
}

function getAnalyticsClient(): AnalyticsClientInstance {
  if (!analyticsClientInstance) {
    throw new Error(
      "Analytics client not initialized. Call initAnalyticsClient() first."
    );
  }
  return analyticsClientInstance;
}

export {
  createAnalyticsClient,
  initAnalyticsClient,
  getAnalyticsClient,
  MIXPANEL_PLUGIN,
};
export type {
  AnalyticsConfig,
  MixpanelPluginConfig,
  PluginConfig,
  AnalyticsClientInstance,
};
